/**
 * Template de prise de besoin : Rapport d'incident TI v1
 * Aligné sur ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0
 * Version complète couvrant tous les aspects d'un rapport d'incident professionnel
 */

import type { IntakeTemplate } from '../templateTypes';

export const templateRapportIncidentV1: IntakeTemplate = {
  id: 'rapport-incident-v1',
  name: 'Prise de besoin Rapport d\'incident TI v1',
  description:
    'Questionnaire complet pour générer un rapport d\'incident TI conforme ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0. Documente la chronologie, l\'analyse de cause racine, les impacts, les mesures de réponse (confinement/éradication/restauration), et les recommandations préventives.',
  documentTypeCode: 'RAPPORT_INCIDENT',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Identification & Classification',
      description: 'Informations de base sur l\'incident et classification initiale',
      icon: 'AlertCircle',
      questions: [
        {
          id: 'nom_incident',
          label: 'Nom de l\'incident/évènement *',
          type: 'text',
          placeholder: 'Ex: Attaque par rançongiciel sur serveur de fichiers',
          required: true,
          helpText: 'ITIL 4 (Incident Management) : Identifiant unique et descriptif de l\'incident. Doit être clair et permettre une identification rapide.',
        },
        {
          id: 'client_affecte',
          label: 'Client(s) affecté(s) (parent/enfants) *',
          type: 'text',
          placeholder: 'Nom du client principal et clients enfants si applicable',
          required: true,
          helpText: 'Identifie l\'étendue organisationnelle de l\'incident. Important pour la communication et la gestion des impacts multi-clients.',
        },
        {
          id: 'numero_billet',
          label: 'Numéro de billet relié à l\'incident',
          type: 'text',
          placeholder: 'Ex: INC-2024-0123',
          helpText: 'ITIL 4 : Référence au système de gestion des incidents (ticketing) pour traçabilité complète.',
        },
        {
          id: 'type_incident',
          label: 'Incident ou évènement *',
          type: 'select',
          options: ['Incident', 'Évènement'],
          required: true,
          helpText: 'ITIL 4 : Un incident est une interruption ou dégradation de service. Un évènement est un changement d\'état détecté. La distinction est importante pour la classification.',
        },
        {
          id: 'description_circonstance',
          label: 'Description/circonstance *',
          type: 'textarea',
          placeholder: 'Décrivez en détail ce qui s\'est passé, comment l\'incident a été découvert, et les circonstances entourant l\'incident...',
          required: true,
          helpText: 'NIST CSF 2.0 (RS.AN-1) : Description factuelle et détaillée essentielle pour l\'analyse post-incident et la documentation complète.',
        },
        {
          id: 'types_incident',
          label: 'Type(s) d\'incident/évènement *',
          type: 'multiselect',
          options: [
            'Hameçonnage',
            'Ingénierie sociale',
            'Logiciel malveillant',
            'Rançongiciel',
            'Déni de service',
            'Attaque Man-in-the-Middle (MITM)',
            'Piratage de compte',
            'Exfiltration ou fuite de données',
            'Erreur d\'un employé',
            'Incident lié à un fournisseur',
            'Panne logicielle ou réseau',
            'Bris matériel',
            'Intrusion physique',
            'Catastrophe naturelle',
            'Autre',
          ],
          required: true,
          helpText: 'NIST CSF 2.0 (RS.AN-2) : Classification selon la nature de la menace. Permet une catégorisation standardisée et une analyse comparative.',
        },
        {
          id: 'personnes_affectees',
          label: 'Personne(s) affectée(s)',
          type: 'textarea',
          placeholder: 'Listez les personnes directement impactées par l\'incident (utilisateurs, clients, employés)...',
          helpText: 'ITIL 4 : Identification des parties prenantes impactées pour communication ciblée et gestion des relations.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Chronologie & Détection',
      description: 'Dates importantes et informations de signalement',
      icon: 'Clock',
      questions: [
        {
          id: 'date_debut_incident',
          label: 'Date de début de l\'incident *',
          type: 'date',
          required: true,
          helpText: 'ITIL 4 : Date réelle de survenue de l\'incident. Essentiel pour le calcul du MTTR (Mean Time To Resolve) et l\'analyse de performance.',
        },
        {
          id: 'heure_debut_incident',
          label: 'Heure de début de l\'incident',
          type: 'time',
          helpText: 'Précision horaire importante pour la chronologie détaillée et l\'analyse forensique.',
        },
        {
          id: 'date_fin_incident',
          label: 'Date de fin de l\'incident',
          type: 'date',
          helpText: 'Date à laquelle l\'incident a été complètement résolu et les services rétablis.',
        },
        {
          id: 'heure_fin_incident',
          label: 'Heure de fin de l\'incident',
          type: 'time',
          helpText: 'Permet le calcul précis de la durée d\'interruption de service.',
        },
        {
          id: 'date_prise_connaissance',
          label: 'Date de la prise de connaissance *',
          type: 'date',
          required: true,
          helpText: 'ITIL 4 : Date à laquelle l\'organisation a pris connaissance de l\'incident. Différent de la date de début si l\'incident n\'a pas été détecté immédiatement.',
        },
        {
          id: 'date_signalement',
          label: 'Date du signalement *',
          type: 'date',
          required: true,
          helpText: 'Date officielle du signalement dans le système de gestion des incidents.',
        },
        {
          id: 'heure_signalement',
          label: 'Heure du signalement *',
          type: 'time',
          required: true,
          helpText: 'NIST CSF 2.0 (RS.CO-1) : Heure précise du signalement pour mesurer le temps de détection.',
        },
        {
          id: 'auteur_signalement',
          label: 'Auteur du signalement *',
          type: 'text',
          placeholder: 'Nom et fonction de la personne ayant signalé l\'incident',
          required: true,
          helpText: 'ITIL 4 : Traçabilité complète du signalement pour audit et amélioration des processus.',
        },
        {
          id: 'methode_signalement',
          label: 'Méthode de signalement *',
          type: 'select',
          options: ['Billet (ERP)', 'Courriel', 'Appel', 'Alerte 365', 'Autre'],
          required: true,
          helpText: 'ITIL 4 : Canal de signalement utilisé. Important pour optimiser les canaux de communication et mesurer leur efficacité.',
        },
        {
          id: 'message_recu',
          label: 'Message reçu',
          type: 'textarea',
          placeholder: 'Contenu du message ou description du signalement initial...',
          helpText: 'Documentation du signalement initial pour référence et analyse.',
        },
        {
          id: 'technicien_premiere_ligne',
          label: 'Technicien de première ligne qui a pris l\'appel initial',
          type: 'text',
          placeholder: 'Nom du technicien',
          helpText: 'ITIL 4 : Identification du technicien de première ligne pour suivi et formation.',
        },
        {
          id: 'technicien_prise_charge',
          label: 'Technicien qui a pris en charge l\'incident/l\'investigation',
          type: 'text',
          placeholder: 'Nom du technicien responsable',
          helpText: 'COBIT 2019 (DSS05.04) : Responsable de l\'investigation pour imputabilité et expertise.',
        },
        {
          id: 'createur_rapport',
          label: 'Nom du créateur du rapport d\'incident/évènement',
          type: 'text',
          placeholder: 'Nom et fonction',
          helpText: 'Responsable de la documentation pour traçabilité et conformité.',
        },
        {
          id: 'journal_gestion',
          label: 'Journal de la gestion de l\'incident',
          type: 'incident-log',
          placeholder: 'Documentez chronologiquement toutes les actions, décisions et interventions prises pendant la gestion de l\'incident...',
          helpText: 'ITIL 4 et NIST CSF 2.0 (RS.CO-2) : Chronologie complète de toutes les actions pour audit, analyse post-incident et amélioration continue.',
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Causes et impacts',
      description: 'Causes probables, impacts sur les actifs et conséquences',
      icon: 'AlertTriangle',
      questions: [
        {
          id: 'causes_probables',
          label: 'Cause(s) probable(s) *',
          type: 'multiselect',
          options: [
            'Erreur humaine',
            'Mauvaise formation',
            'Problème de configuration',
            'Exploitation d\'une vulnérabilité',
            'Erreur logicielle/Bug',
            'Défaillance matérielle',
            'Manque de capacité/Performance',
            'Problème de sécurité',
            'Incident chez un fournisseur',
            'Catastrophe naturelle',
            'Mise à jour ou changement récent',
            'Autre',
            'Cause inconnue',
          ],
          required: true,
          helpText: 'ITIL 4 (Problem Management) : Analyse de cause racine initiale. Essentiel pour identifier les causes systémiques et prévenir les récurrences.',
        },
        {
          id: 'impacts_actifs',
          label: 'Impact(s) sur les actifs *',
          type: 'multiselect',
          options: [
            'Disponibilité',
            'Intégrité',
            'Confidentialité',
            'Authenticité',
            'Principe de non-répudiation',
            'Traçabilité/Preuve',
          ],
          required: true,
          helpText: 'ISO 27001 (A.5.1.1) : Classification selon les principes de sécurité de l\'information (CIA triad +). Essentiel pour évaluer la gravité.',
        },
        {
          id: 'consequences_entreprise',
          label: 'Conséquence(s) pour l\'entreprise *',
          type: 'multiselect',
          options: [
            'Ajustement des pratiques opérationnelles',
            'Dommage sur votre réputation',
            'Infraction/Sanction légale',
            'Interruption de service',
            'Répercussions financières',
            'Vol de données sensibles',
            'Aucune',
          ],
          required: true,
          helpText: 'COBIT 2019 (APO12.05) : Évaluation de l\'impact business pour priorisation et communication aux parties prenantes.',
        },
        {
          id: 'actifs_touches',
          label: 'Actif(s) touché(s)',
          type: 'textarea',
          placeholder: 'Listez les actifs TI spécifiques touchés (serveurs, bases de données, applications, réseaux)...',
          helpText: 'ITIL 4 (Service Asset Management) : Inventaire des actifs impactés pour évaluation complète des dommages.',
        },
        {
          id: 'donnees_sensibles_touchees',
          label: 'Donnée(s) sensible(s) touchée(s)',
          type: 'textarea',
          placeholder: 'Types de données sensibles affectées (renseignements personnels, données financières, secrets commerciaux)...',
          helpText: 'ISO 27001 (A.8.2.1) et Loi 25 : Identification des données sensibles pour conformité et obligations de notification.',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Réponse & Résolution',
      description: 'Mesures de confinement, éradication, restauration et résolution',
      icon: 'Shield',
      questions: [
        {
          id: 'mesures_confinement',
          label: 'Quelles mesures de confinement ont été effectuées ?',
          type: 'multiselect',
          options: [
            'Changement de mot de passe',
            'Déconnexion des sessions',
            'Coupure d\'internet',
            'Reconfiguration du 2FA',
            'Modification de la configuration',
            'Isolation du réseau',
            'Désactivation de comptes',
            'Autre',
          ],
          helpText: 'NIST CSF 2.0 (RS.MI-1) : Actions immédiates pour limiter l\'étendue et l\'impact de l\'incident. Essentiel pour la réponse rapide.',
        },
        {
          id: 'verifications_analyses',
          label: 'Quelles vérifications et analyses ont été réalisées pour comprendre l\'incident ?',
          type: 'multiselect',
          options: [
            'Vérification des journaux',
            'Vérification des règles',
            'Analyse du flux de courriers',
            'Audit Microsoft',
            'Analyse forensique',
            'Revue des configurations',
            'Autre',
          ],
          helpText: 'NIST CSF 2.0 (RS.AN) : Investigation approfondie pour comprendre l\'incident et identifier la cause racine.',
        },
        {
          id: 'droits_acces_excessifs',
          label: 'Des droits d\'accès excessifs ont-ils facilité l\'incident ?',
          type: 'select',
          options: ['Oui, droits rectifiés', 'Oui, droits non rectifiés', 'Non', 'Ne sais pas'],
          helpText: 'COBIT 2019 (DSS05.04) et NIST CSF 2.0 (PR.AC-3) : Évaluation du principe du moindre privilège. Important pour prévenir les récurrences.',
        },
        {
          id: 'vulnerabilites_logicielles',
          label: 'Des vulnérabilités logicielles ont-elles joué un rôle dans l\'incident ?',
          type: 'select',
          options: ['Oui, corrigées', 'Oui, non corrigées', 'Non', 'Ne sais pas'],
          helpText: 'NIST CSF 2.0 (PR.IP-12) : Gestion des vulnérabilités. Essentiel pour la sécurité proactive.',
        },
        {
          id: 'mesures_eradication',
          label: 'Quelles mesures d\'éradication ont été effectuées ?',
          type: 'multiselect',
          options: [
            'Mise à jour des systèmes',
            'Balayage antivirus',
            'Modification de la configuration',
            'Réinitialisation complète',
            'Suppression de logiciels malveillants',
            'Correction de vulnérabilités',
            'Autre',
          ],
          helpText: 'NIST CSF 2.0 (RS.MI-2) : Actions pour éliminer complètement la menace et restaurer un état sécurisé.',
        },
        {
          id: 'logiciel_malveillant_eradique',
          label: 'Un logiciel malveillant était-il impliqué et a-t-il été complètement éradiqué?',
          type: 'select',
          options: ['Oui, complète', 'Oui, incomplète', 'Non'],
          helpText: 'NIST CSF 2.0 (RS.MI-2) : Vérification de l\'éradication complète pour éviter la récurrence.',
        },
        {
          id: 'actions_restauration',
          label: 'Quelles actions ont été réalisées pour restaurer les services après l\'incident ?',
          type: 'multiselect',
          options: [
            'Réactivation des accès',
            'Reconnexion à internet',
            'Restauration de la sauvegarde',
            'Redémarrage des services',
            'Reconfiguration des systèmes',
            'Autre',
          ],
          helpText: 'ITIL 4 (Incident Management) et NIST CSF 2.0 (RC.RP-1) : Actions de restauration pour rétablir les services normaux.',
        },
        {
          id: 'duree_interruption',
          label: 'Combien de temps le service a-t-il été interrompu ?',
          type: 'select',
          options: [
            '0h',
            '1h',
            '2h',
            '3h',
            '4h',
            '5h',
            '6h',
            '7h',
            '8h',
            '9h',
            '10h',
            '+ de 10h',
          ],
          helpText: 'ITIL 4 : Calcul du temps d\'interruption pour mesurer l\'impact business et la performance de résolution.',
        },
        {
          id: 'date_resolution',
          label: 'Date de résolution',
          type: 'date',
          helpText: 'ITIL 4 : Date officielle de résolution de l\'incident.',
        },
        {
          id: 'heure_resolution',
          label: 'Heure de résolution',
          type: 'time',
          helpText: 'Permet le calcul précis du MTTR (Mean Time To Resolve).',
        },
        {
          id: 'verification_lendemain',
          label: 'Une vérification a-t-elle été effectuée le lendemain de l\'incident ?',
          type: 'select',
          options: ['Oui', 'Non'],
          followUp: {
            whenValue: 'Oui',
            id: 'verification_lendemain_details',
            label: 'Précisez la vérification effectuée',
            placeholder: 'Décrivez la vérification réalisée le lendemain...',
            type: 'textarea',
          },
          helpText: 'ITIL 4 : Vérification post-résolution pour s\'assurer de la stabilité et détecter les récurrences.',
        },
        {
          id: 'verification_semaine',
          label: 'Une vérification a-t-elle été réalisée une semaine après l\'incident ?',
          type: 'select',
          options: ['Oui', 'Non'],
          followUp: {
            whenValue: 'Oui',
            id: 'verification_semaine_details',
            label: 'Précisez la vérification effectuée',
            placeholder: 'Décrivez la vérification réalisée une semaine après...',
            type: 'textarea',
          },
          helpText: 'ITIL 4 : Vérification à plus long terme pour validation complète de la résolution.',
        },
      ],
    },
    {
      id: 'phase5',
      title: 'Conformité & Documentation - Loi 25',
      description: 'Journalisation et recommandations',
      icon: 'FileCheck',
      questions: [
        {
          id: 'type_renseignements',
          label: 'Type des renseignements visés (Loi 25)',
          type: 'multiselect',
          options: [
            'Enregistrements',
            'Renseignements biométriques',
            'Renseignements de géolocalisation',
            'Renseignements d\'emploi',
            'Renseignements de profil',
            'Renseignements d\'identité',
            'Renseignements familiaux/sociaux',
            'Renseignements financiers',
            'Renseignements judiciaires',
            'Renseignements médicaux',
            'Renseignements scolaires',
            'Renseignements technologiques',
          ],
          helpText: 'Loi 25 : Classification des renseignements personnels touchés pour déterminer les obligations de notification.',
        },
        {
          id: 'nombre_personnes_concernees',
          label: 'Nombre de personnes concernées (Loi 25)',
          type: 'number',
          placeholder: 'Nombre approximatif',
          helpText: 'Loi 25 : Détermine si une notification à la CAI est requise (seuil de 100 personnes).',
        },
        {
          id: 'sensibilite_renseignements',
          label: 'Sensibilité des renseignements personnels concernés',
          type: 'select',
          options: ['Faible', 'Moyenne', 'Élevée', 'Très élevée'],
          helpText: 'Loi 25 : Évaluation de la sensibilité pour déterminer la gravité et les obligations légales.',
        },
        {
          id: 'utilisations_malveillantes',
          label: 'Utilisations malveillantes possibles des renseignements',
          type: 'multiselect',
          options: [
            'Conséquence négative sur le dossier de crédit',
            'Dommages moraux (atteinte à la réputation, humiliation, diffamation, discrimination)',
            'Fraude ou perte financière',
            'Perte d\'emploi ou perte d\'occasions d\'emploi',
            'Perte liée aux affaires',
            'Répercussions sur la santé physique ou psychologique (stress)',
            'Vol ou usurpation d\'identité',
            'Autre (explication requise)',
          ],
          helpText: 'Loi 25 : Évaluation des risques de préjudice pour déterminer les obligations de notification.',
        },
        {
          id: 'probabilite_utilisation_prejudiciable',
          label: 'Probabilité qu\'ils soient utilisés à des fins préjudiciables',
          type: 'select',
          options: ['Nulle', 'Faible', 'Moyenne', 'Élevée'],
          helpText: 'Loi 25 : Évaluation du risque pour déterminer la nécessité de notification.',
        },
        {
          id: 'prejudice',
          label: 'Préjudice',
          type: 'select',
          options: ['Non', 'Oui'],
          helpText: 'Loi 25 : Confirmation de préjudice réel pour obligations légales.',
        },
        {
          id: 'communique_cai',
          label: 'Communiqué CAI',
          type: 'select',
          options: ['Non', 'Oui'],
          helpText: 'Loi 25 : Notification à la Commission d\'accès à l\'information si requis.',
        },
        {
          id: 'date_communication_cai',
          label: 'Date de communication CAI',
          type: 'date',
          helpText: 'Loi 25 : Date de notification officielle à la CAI (obligatoire dans certains cas).',
        },
        {
          id: 'personnes_avisees',
          label: 'Personnes avisées',
          type: 'select',
          options: ['Non', 'Oui'],
          helpText: 'Loi 25 : Notification aux personnes concernées si préjudice réel ou risque élevé.',
        },
        {
          id: 'date_communication_personnes',
          label: 'Date de communication aux personnes',
          type: 'date',
          helpText: 'Loi 25 : Date de notification aux personnes concernées.',
        },
        {
          id: 'recommandations_superviseur',
          label: 'Recommandations et commentaires du superviseur',
          type: 'textarea',
          placeholder: 'Recommandations du superviseur pour prévention future et amélioration des processus...',
          helpText: 'ITIL 4 (Problem Management) : Recommandations pour prévenir les récurrences et améliorer les processus de gestion d\'incidents.',
        },
      ],
    },
  ],
};





